import React  from "react";
import { useRef, useState, useEffect } from "react";
import "./ClientsEnquirySection.css";
import './universal.css';
import clientsData from '../data/clients.json';
import { sendEmailJS } from "../utils/smtp";

const logoFallback = "data:image/gif;base64,R0lGODlhAQABAAAAACw="; // transparent

const ctx = require.context('../assets/clients', false, /\.(png|jpe?g|webp|svg)$/);

function resolveImagePath(path) {
  if (!path) return logoFallback;
  if (/^https?:\/\//i.test(path)) return path;
  try {
    return ctx(`./${path.split('/').pop()}`); // assuming path is like "src/assets/clients/abb.png", take the filename
  } catch (err) {
    return logoFallback;
  }
}

const logos = clientsData.filter(c => c.home).map(c => ({
  name: c.name,
  src: resolveImagePath(c.imageUrl)
}));

export default function ClientsEnquirySection() {
  const recaptchaRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [recaptchaReady, setRecaptchaReady] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [requirement, setRequirement] = useState("");
  const [sendStatus, setSendStatus] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
      if (window.grecaptcha && typeof window.grecaptcha.render === "function") {
        setRecaptchaReady(true);
        return undefined;
      }
      if (document.getElementById("recaptcha-script")) return undefined;
      const script = document.createElement("script");
      script.id = "recaptcha-script";
      script.src = "https://www.google.com/recaptcha/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.onload = () => setRecaptchaReady(true);
      document.body.appendChild(script);
      return () => {
        document.body.removeChild(script);
      };
    }, []);

    useEffect(() => {
      if (!recaptchaReady || !recaptchaRef.current || !window.grecaptcha || typeof window.grecaptcha.render !== "function") return undefined;
      widgetIdRef.current = window.grecaptcha.render(recaptchaRef.current, {
        sitekey: process.env.REACT_RECAPTCHA_SITE_KEY || "RECAPTCHA_SITE_KEY",
        callback: token => setRecaptchaToken(token),
        "expired-callback": () => setRecaptchaToken(""),
        "error-callback": () => setRecaptchaToken("")
      });
      return () => {
        if (window.grecaptcha && widgetIdRef.current !== null) {
          window.grecaptcha.reset(widgetIdRef.current);
        }
      };
    }, [recaptchaReady]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!recaptchaToken) {
      alert("Please complete the reCAPTCHA.");
      return;
    }
    setSending(true);
    setSendStatus("");

    const body = `
      <h2>Client enquiry submission</h2>
      <p><strong>Name:</strong> ${name}</p>
      <p><strong>Email:</strong> ${email}</p>
      <p><strong>Requirement:</strong><br/>${requirement.replace(/\n/g, "<br/>")}</p>
      <p><strong>reCAPTCHA token:</strong> ${recaptchaToken}</p>
    `;

    try {
      await sendEmailJS({
        from_name: name,
        from_email: email,
        requirement,
        message_html: body,
      });
      setSendStatus("Enquiry sent successfully.");
      setName("");
      setEmail("");
      setRequirement("");
      setRecaptchaToken("");
      if (window.grecaptcha && widgetIdRef.current !== null) {
        window.grecaptcha.reset(widgetIdRef.current);
      }
    } catch (error) {
      console.error(error);
      setSendStatus("Failed to send enquiry. Please try again later.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="clients-enquiry-full">

      





        <div className="fw-inner container">
        <div className="row  align-items-center">

          {/* LEFT SIDE — 2/3 */}
          <div className="col-12 col-lg-8 ce-left">
            <span className="ce-tag">Trusted By</span>
            <h2 className="sec-title">Our Valued Clients</h2>
            <p className="ce-description">
              Delivering precision-engineered instrumentation valves & fittings
              to leading industrial partners across Australia.
            </p>

            <div className="ce-logos">
              {logos.map((logo, idx) => (
                <div key={idx} className="ce-logo-card">
                  <img
                    src={logo.src}
                    alt={logo.name}
                    loading="lazy"
                    onError={(e) => { e.currentTarget.src = logoFallback; }}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT SIDE — 1/3 */}
          <div className="col-12 col-lg-4">
            <div className="ce-right">
                <div className="enquiryHeader">
              <div className="ce-form-header">
                <h3 >Send an Enquiry</h3>
                <p>Our team will respond within 24 hours.</p>
              </div>
              </div>
              <div className="ce-form-container">

              <form className="ce-form" onSubmit={handleSubmit}>
                <div className="ce-field">
                  <label>Name</label>
                  <input
                    type="text"
                    placeholder="Your Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>

                <div className="ce-field">
                  <label>Email</label>
                  <input
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="ce-field">
                  <label>Requirement</label>
                  <textarea
                    rows="4"
                    placeholder="Describe your requirement"
                    value={requirement}
                    onChange={(e) => setRequirement(e.target.value)}
                    required
                  ></textarea>
                </div>

                <div className="ce-recaptcha" ref={recaptchaRef} style={{ marginBottom: "12px" }} />

                <button type="submit" className="ce-btn" disabled={!recaptchaToken || sending}>
                  {sending ? "Sending..." : "Submit Enquiry"}
                </button>
                {sendStatus && <div className="send-status">{sendStatus}</div>}
              </form>
            </div>
            </div>
          </div>

        </div>
        </div>
      {/* </div> */}
    </section>
  );
}